// Base class for D3 bar charts.
// Subclasses should override hooks (getBarColor, onBarClick, renderTooltipContent, formatXAxisLabel, getGroupKey)
// and must NOT contain any domain-specific knowledge here.
//
// Expected state shape:
//   state.data: Array<{ value: number, label: string, shortLabel?: string, key: any, meta?: any }>
//   state.options: {
//     colors?: { default: string, hover: string } | { palette: {...}, classify: (d) => string },
//     valueFormat?: string,            // e.g. 'currency' or a d3 format spec like ',.2f'
//     axisTickFormat?: string,         // d3 format spec for Y axis ticks
//     showValueLabels?: boolean,
//     showLegend?: boolean,
//     legend?: Array<{ label: string, colorKey: string }>,
//     showAxes?: boolean,              // master switch for X+Y axes (sparkline mode when false)
//     showXAxis?: boolean,             // overrides showAxes for X
//     showYAxis?: boolean,             // overrides showAxes for Y
//     yAxisSide?: 'left' | 'right',    // which side to render the Y axis on (default: 'right')
//     hideZeroTick?: boolean,          // if true, the 0 tick (and its grid line) is not rendered on the Y axis
//     barWidth?: number | null,        // desired bar width in px; if null, scaleBand fills container
//     barPadding?: number,
//     barCornerRadius?: number,
//     height?: number,                 // height in px; if absent, taken from container
//     margin?: { top, right, bottom, left }
//   }
export class BarChartD3 extends Tango.Component {
    init() {
        this.state = this.state || {};
        this._resizeObserver = null;
        this._resizeRaf = null;
        this._tooltipNode = null;
    }

    // ===== Hooks for subclasses =====

    // Subclasses override to provide their default options.
    // These are merged with state.options (server-supplied wins).
    getDefaultOptions() {
        return {};
    }

    // Return CSS color string for a bar.
    getBarColor(d, isHover) {
        const opts = this._opts();
        const colors = opts.colors || { default: '#4CAF50', hover: '#388E3C' };
        if (typeof colors.classify === 'function') {
            const key = colors.classify(d);
            const palette = colors.palette || {};
            const entry = palette[key] || {};
            return isHover ? (entry.hover || entry.default) : entry.default;
        }
        return isHover ? colors.hover : colors.default;
    }

    // Click handler. Default: no-op.
    onBarClick(d, event) { }

    // Hover/leave hooks for additional side effects (color change is handled in base).
    onBarHover(d, event) { }
    onBarLeave(d, event) { }

    // Return the tooltip content for a bar — either an HTML string, a DOM Node,
    // or null/undefined to disable the tooltip for this bar.
    // The base class only positions the tooltip; ALL appearance (background, padding,
    // borders, fonts, layout) is the subclass's responsibility.
    renderTooltipContent(d) { return null; }

    // Group adjacent points (e.g. months by year) so that x-axis labels can decide
    // to render a group prefix only on the first point of each group. Return any
    // comparable group key, or null/undefined to disable grouping.
    // Default: read d.meta.group if present.
    getGroupKey(d, idx) {
        return d && d.meta ? d.meta.group : undefined;
    }

    // Decide what label to show on X axis.
    // Return a string to render, or null to skip this tick.
    // strategy: 'full' | 'short' | 'rotated' — hint about thinning/rotation chosen by base class.
    // isFirstInGroup: true if this point starts a new group (per getGroupKey), or true when grouping is disabled.
    formatXAxisLabel(d, idx, totalBars, slotWidth, strategy, isFirstInGroup) {
        if (strategy === 'short' && d.shortLabel) return d.shortLabel;
        return d.label;
    }

    // ===== Lifecycle =====

    widgetDidMount() {
        const root = document.getElementById(this.root);
        if (!root) return;
        this._root = root;
        this._render();

        // Adapt to container resize (mobile rotation, layout changes).
        if (typeof ResizeObserver !== 'undefined') {
            this._resizeObserver = new ResizeObserver(() => this._scheduleRerender());
            this._resizeObserver.observe(root);
        }
    }

    widgetContentChanged() {
        // If the host re-renders the container/state, redraw.
        this._render();
    }

    _scheduleRerender() {
        if (this._resizeRaf) cancelAnimationFrame(this._resizeRaf);
        this._resizeRaf = requestAnimationFrame(() => {
            this._resizeRaf = null;
            this._render();
        });
    }

    // ===== Rendering =====

    _opts() {
        // Merge subclass defaults with server-supplied options (server wins).
        if (!this._mergedOpts) {
            const defaults = this.getDefaultOptions() || {};
            const stateOpts = (this.state && this.state.options) || {};
            this._mergedOpts = Object.assign({}, defaults, stateOpts);
        }
        return this._mergedOpts;
    }

    _resolvedAxisFlags() {
        const o = this._opts();
        const showAxes = o.showAxes !== false; // default true
        return {
            showXAxis: o.showXAxis !== undefined ? o.showXAxis : showAxes,
            showYAxis: o.showYAxis !== undefined ? o.showYAxis : showAxes
        };
    }

    _resolvedYAxisSide() {
        const o = this._opts();
        // Default: right. The two charts that exist in the codebase both want the Y axis on the right;
        // subclasses that need it on the left must explicitly set yAxisSide: 'left'.
        return o.yAxisSide === 'left' ? 'left' : 'right';
    }

    _resolvedMargin() {
        const o = this._opts();
        const { showXAxis, showYAxis } = this._resolvedAxisFlags();
        const yAxisSide = this._resolvedYAxisSide();
        // Sensible defaults that shrink in sparkline mode.
        // The Y axis reserves space on whichever side it lives on; the opposite side gets a small breathing margin.
        const yAxisGutter = showYAxis ? 50 : 4;
        const oppositeGutter = 6;
        const def = {
            top: o.showValueLabels ? 16 : 6,
            right: yAxisSide === 'right' ? yAxisGutter : (o.showLegend ? 20 : oppositeGutter),
            bottom: showXAxis ? 28 : 4,
            left: yAxisSide === 'left' ? yAxisGutter : 4
        };
        return Object.assign(def, o.margin || {});
    }

    _formatValue(v) {
        const o = this._opts();
        const fmt = o.valueFormat;
        if (!fmt) return d3.format(',.2f')(v);
        if (fmt === 'currency') return '$' + d3.format(',.2f')(v);
        try { return d3.format(fmt)(v); } catch (e) { return String(v); }
    }

    _formatTick(v) {
        const o = this._opts();
        const fmt = o.axisTickFormat;
        if (fmt) {
            try { return d3.format(fmt)(v); } catch (e) { /* fallthrough */ }
        }
        if (o.valueFormat === 'currency') return '$' + d3.format(',.0f')(v);
        return d3.format(',.0f')(v);
    }

    _render() {
        const root = this._root;
        if (!root) return;
        const data = (this.state && this.state.data) || [];

        // Reset options cache so state changes are picked up.
        this._mergedOpts = null;

        // Reset tooltip — after a server-driven re-render the previous tooltip node
        // may be detached from the live DOM (or attached to a stale root). Drop it
        // so _ensureTooltip() recreates it inside the current root.
        if (this._tooltipNode) {
            this._tooltipNode.remove();
            this._tooltipNode = null;
        }

        // Wipe previous render.
        d3.select(root).selectAll('svg').remove();

        if (data.length === 0) return;

        const opts = this._opts();
        const { showXAxis, showYAxis } = this._resolvedAxisFlags();
        const yAxisSide = this._resolvedYAxisSide();
        const margin = this._resolvedMargin();

        const containerWidth = root.clientWidth || 600;
        const containerHeight = opts.height || root.clientHeight || 300;

        const width = Math.max(0, containerWidth - margin.left - margin.right);
        const height = Math.max(0, containerHeight - margin.top - margin.bottom);

        const svg = d3.select(root)
            .append('svg')
            .attr('width', containerWidth)
            .attr('height', containerHeight)
            .append('g')
            .attr('transform', `translate(${margin.left},${margin.top})`);

        // ===== Scales =====
        // Two modes:
        //   (a) opts.barWidth set — desired bar width, pack bars from the left, used for sparkline-style charts.
        //       If the desired total (barWidth + gap) * N exceeds the available width,
        //       bars and gaps are shrunk proportionally; gap is clamped to a minimum of 1px,
        //       and the bar width is re-derived so that bar + minGap fits the per-bar slot.
        //   (b) auto — d3.scaleBand fills the available width.
        let xScale;
        if (opts.barWidth) {
            const desiredBarW = opts.barWidth;
            const padRatio = opts.barPadding != null ? opts.barPadding : 0.5;
            const desiredGap = padRatio * desiredBarW;
            const desiredStep = desiredBarW + desiredGap;
            const desiredTotal = desiredStep * data.length;

            let fixedBarW, fixedStep;
            if (desiredTotal <= width || width <= 0) {
                // Fits — use desired sizes (or width unknown, fall back to desired).
                fixedBarW = desiredBarW;
                fixedStep = desiredStep;
            } else {
                // Shrink proportionally; clamp gap to >= 1px.
                const scale = width / desiredTotal;
                const minGap = 1;
                let gap = desiredGap * scale;
                let barW = desiredBarW * scale;
                if (gap < minGap) {
                    // Re-derive bar width so that bar + minGap fits the slot.
                    gap = minGap;
                    barW = Math.max(1, (width / data.length) - gap);
                }
                fixedBarW = Math.max(1, barW);
                fixedStep = fixedBarW + gap;
            }

            // Manual scale — d3.scaleBand stretches by design, which is wrong here.
            const scaleFn = (i) => i * fixedStep;
            scaleFn.bandwidth = () => fixedBarW;
            scaleFn.step = () => fixedStep;
            scaleFn.range = () => [0, fixedStep * data.length];
            scaleFn.domain = () => data.map((_, i) => i);
            xScale = scaleFn;
        } else {
            xScale = d3.scaleBand()
                .domain(data.map((d, i) => i))
                .range([0, width])
                .padding(opts.barPadding != null ? opts.barPadding : 0.2);
        }

        // Actual horizontal space occupied by bars. In barWidth mode the bars are packed
        // from the left and may not fill the full plot width — Y axis (when on the right),
        // grid lines and X axis line should hug the bars rather than the plot edge.
        const usedWidth = (opts.barWidth && xScale.step)
            ? Math.min(width, xScale.step() * data.length)
            : width;

        const yMax = d3.max(data, d => d.value) || 0;

        const yScale = d3.scaleLinear()
            .domain([0, yMax > 0 ? yMax * 1.1 : 1])
            .nice()
            .range([height, 0]);

        // ===== Y axis =====
        if (showYAxis) {
            const tickCount = Math.max(2, Math.min(6, Math.floor(height / 30)));
            let ticks = yScale.ticks(tickCount);
            if (opts.hideZeroTick) {
                ticks = ticks.filter(v => v !== 0);
            }

            const yAxis = (yAxisSide === 'right' ? d3.axisRight(yScale) : d3.axisLeft(yScale))
                .tickValues(ticks)
                .tickFormat(v => this._formatTick(v));

            const yAxisG = svg.append('g')
                .attr('class', 'y-axis')
                .call(yAxis);

            if (yAxisSide === 'right') {
                yAxisG.attr('transform', `translate(${usedWidth},0)`);
            }

            yAxisG.selectAll('text').style('font-size', '11px');

            // Grid lines — share the same tick set as the axis. Span the bars' actual
            // extent (usedWidth) so they line up with the Y axis instead of the plot edge.
            svg.append('g')
                .attr('class', 'grid')
                .selectAll('line')
                .data(ticks)
                .enter()
                .append('line')
                .attr('x1', 0)
                .attr('x2', usedWidth)
                .attr('y1', d => yScale(d))
                .attr('y2', d => yScale(d))
                .attr('stroke', '#e0e0e0')
                .attr('stroke-dasharray', '3,3');
        }

        // ===== Bars =====
        const self = this;
        const bandwidth = xScale.bandwidth();

        const bars = svg.selectAll('.bar')
            .data(data)
            .enter()
            .append('rect')
            .attr('class', 'bar')
            .attr('x', (d, i) => xScale(i))
            .attr('y', d => yScale(d.value))
            .attr('width', bandwidth)
            .attr('height', d => Math.max(0, height - yScale(d.value)))
            .attr('fill', d => self.getBarColor(d, false))
            .attr('rx', opts.barCornerRadius != null ? opts.barCornerRadius : 2)
            .attr('ry', opts.barCornerRadius != null ? opts.barCornerRadius : 2)
            .style('cursor', 'pointer');

        // Hover/click wiring.
        bars
            .on('mouseenter', function (event, d) {
                d3.select(this).attr('fill', self.getBarColor(d, true));
                self._showTooltip(d, event);
                self.onBarHover(d, event);
            })
            .on('mousemove', function (event, d) {
                self._moveTooltip(event);
            })
            .on('mouseleave', function (event, d) {
                d3.select(this).attr('fill', self.getBarColor(d, false));
                self._hideTooltip();
                self.onBarLeave(d, event);
            })
            .on('click', function (event, d) {
                self.onBarClick(d, event);
            });

        // ===== Value labels above bars =====
        if (opts.showValueLabels) {
            svg.selectAll('.bar-label')
                .data(data)
                .enter()
                .append('text')
                .attr('class', 'bar-label')
                .attr('x', (d, i) => xScale(i) + bandwidth / 2)
                .attr('y', d => yScale(d.value) - 4)
                .attr('text-anchor', 'middle')
                .style('font-size', '10px')
                .style('fill', '#666')
                .text(d => d.value > 0 ? this._formatValue(d.value) : '');
        }

        // ===== X axis (custom, adaptive) =====
        if (showXAxis) {
            this._renderXAxis(svg, data, xScale, height, usedWidth);
        }

        // ===== Legend =====
        if (opts.showLegend && Array.isArray(opts.legend) && opts.legend.length > 0) {
            this._renderLegend(svg, usedWidth, opts.legend);
        }
    }

    // Custom X axis with adaptive label thinning + rotation.
    _renderXAxis(svg, data, xScale, height, usedWidth) {
        const slotWidth = xScale.step ? xScale.step() : xScale.bandwidth();
        const bandwidth = xScale.bandwidth();
        const total = data.length;

        // Group membership for first-in-group labelling. When getGroupKey returns
        // undefined/null for any point, grouping is treated as disabled and
        // isFirstInGroup is true everywhere — preserving prior behaviour.
        const groupKeys = data.map((d, i) => this.getGroupKey(d, i));
        const groupingEnabled = groupKeys.some(k => k !== undefined && k !== null);
        const isFirstInGroup = (i) => {
            if (!groupingEnabled) return true;
            const k = groupKeys[i];
            if (k === undefined || k === null) return true;
            return i === 0 || groupKeys[i - 1] !== k;
        };

        // Estimate label width in pixels using a hidden measurement node.
        const measure = svg.append('text')
            .attr('class', 'x-axis-measure')
            .style('font-size', '11px')
            .style('visibility', 'hidden');

        const measureWidth = (text) => {
            measure.text(text);
            try { return measure.node().getComputedTextLength() || (text.length * 7); }
            catch (e) { return text.length * 7; }
        };

        // Decide strategy: 'full' (all labels), 'short' (use shortLabel where available),
        // 'thinned' (every Nth label), 'rotated' (-45 deg), or combination.
        // Algorithm:
        //   1. Try full labels horizontal — if maxLabelWidth + gap fits in slotWidth, use full text.
        //   2. Else try shortLabel horizontal — same check.
        //   3. Else short + thinning; if thinning would be too aggressive on a small dataset, rotate.
        // The actual thinning (which ticks get a label) is decided further below, right-to-left,
        // so the rightmost tick always keeps its label.

        const fullLabels = data.map((d, i) => this.formatXAxisLabel(d, i, total, slotWidth, 'full', isFirstInGroup(i)) || '');
        const shortLabels = data.map((d, i) => this.formatXAxisLabel(d, i, total, slotWidth, 'short', isFirstInGroup(i)) || fullLabels[i]);

        const maxFullW = d3.max(fullLabels, l => measureWidth(l)) || 0;
        const maxShortW = d3.max(shortLabels, l => measureWidth(l)) || 0;

        let strategy, useLabels, rotate = false;
        const gap = 6;

        if (maxFullW + gap <= slotWidth) {
            strategy = 'full';
            useLabels = fullLabels;
        } else if (maxShortW + gap <= slotWidth) {
            strategy = 'short';
            useLabels = shortLabels;
        } else {
            // Too dense for horizontal labels at every tick — thin with short labels.
            strategy = 'short';
            useLabels = shortLabels;
            // If thinning would be too aggressive on a small dataset, rotate instead of thinning.
            const step = Math.ceil((maxShortW + gap) / slotWidth);
            if (step > 2 && total <= 30) {
                rotate = true;
                strategy = 'rotated';
                // Use full labels when rotated (more readable).
                useLabels = data.map((d, i) => this.formatXAxisLabel(d, i, total, slotWidth, 'rotated', isFirstInGroup(i)) || fullLabels[i]);
            }
        }

        // Widest label we will actually draw — drives the minimum spacing between labels.
        const usedW = d3.max(useLabels, l => (l ? measureWidth(l) : 0)) || 0;

        measure.remove();

        // Decide which ticks get a label, anchored on the RIGHT and adapting to the local
        // spacing. Candidates are the ticks that resolved to a non-empty label — in month
        // mode that's the first (leftmost) bar of each year group, which can sit at irregular
        // distances because years hold a different number of paid months.
        //
        // Walk the candidates right-to-left. The rightmost candidate — the first bar of the
        // group the last bar belongs to — always gets a label. Going left, the next candidate
        // is labelled only once `minInterval` px of room has opened up since the previously
        // placed label. `minInterval` is derived from the bar width (at least one empty bar
        // between labels) but never smaller than the widest label, so labels never touch.
        // This keeps labels as dense as they fit locally instead of forcing one global step.
        // Rotated labels don't overlap horizontally, so every candidate is kept.
        const centerX = (i) => xScale(i) + bandwidth / 2;
        const barSlot = (xScale.step ? xScale.step() : slotWidth) || slotWidth;
        const minInterval = Math.max(usedW + gap, 2 * barSlot);

        const candidates = [];
        for (let i = 0; i < total; i++) if (useLabels[i]) candidates.push(i);

        const shown = [];
        let lastX = null;
        for (let c = candidates.length - 1; c >= 0; c--) {
            const i = candidates[c];
            const x = centerX(i);
            if (rotate || lastX === null || (lastX - x) >= minInterval) {
                shown.push(i);
                lastX = x;
            }
        }
        shown.reverse(); // draw left-to-right (cosmetic; order does not matter to SVG)

        const axisG = svg.append('g')
            .attr('class', 'x-axis')
            .attr('transform', `translate(0,${height})`);

        // Axis line — ends where the bars end (usedWidth), not at the plot edge.
        const range = xScale.range();
        axisG.append('line')
            .attr('x1', range[0])
            .attr('x2', usedWidth != null ? usedWidth : range[1])
            .attr('y1', 0)
            .attr('y2', 0)
            .attr('stroke', '#999');

        for (const i of shown) {
            const label = useLabels[i];

            const cx = xScale(i) + bandwidth / 2;

            // Tick mark.
            axisG.append('line')
                .attr('x1', cx).attr('x2', cx)
                .attr('y1', 0).attr('y2', 4)
                .attr('stroke', '#999');

            const text = axisG.append('text')
                .attr('x', cx)
                .attr('y', rotate ? 8 : 16)
                .style('font-size', '11px')
                .style('fill', '#555')
                .text(label);

            if (rotate) {
                text.attr('text-anchor', 'end')
                    .attr('transform', `rotate(-45, ${cx}, 8)`);
            } else {
                text.attr('text-anchor', 'middle');
            }
        }
    }

    _renderLegend(svg, width, legend) {
        const opts = this._opts();
        const palette = (opts.colors && opts.colors.palette) || {};
        const itemWidth = 80;
        const totalWidth = legend.length * itemWidth;

        const g = svg.append('g')
            .attr('class', 'legend')
            .attr('transform', `translate(${Math.max(0, width - totalWidth)}, -10)`);

        legend.forEach((item, idx) => {
            const color = (palette[item.colorKey] && palette[item.colorKey].default)
                || item.color
                || '#4CAF50';
            const x = idx * itemWidth;

            g.append('rect')
                .attr('x', x).attr('y', 0)
                .attr('width', 12).attr('height', 12)
                .attr('fill', color)
                .attr('rx', 2);

            g.append('text')
                .attr('x', x + 18).attr('y', 10)
                .style('font-size', '11px')
                .text(item.label);
        });
    }

    // ===== Tooltip =====
    //
    // The base class only owns the *behavior* of the tooltip: creating an absolutely
    // positioned, layout-participating container; placing it relative to the cursor
    // while keeping it inside the chart root; and showing/hiding it on hover.
    //
    // The *appearance* (background, border, padding, fonts, layout, swatches, icons,
    // multi-line content) is fully delegated to the subclass via renderTooltipContent,
    // which may return either an HTML string or a DOM Node.
    //
    // Note: the container uses visibility:hidden (not display:none) so that
    // offsetWidth/offsetHeight measurements remain valid for positioning.

    _ensureTooltip() {
        if (this._tooltipNode) return this._tooltipNode;
        const node = d3.select(this._root)
            .append('div')
            .attr('class', 'barchart-tooltip-host')
            .style('position', 'absolute')
            .style('visibility', 'hidden')
            .style('pointer-events', 'none')
            .style('z-index', '1000');
        // Make sure the root is positioned so the tooltip anchors correctly.
        const rootStyle = window.getComputedStyle(this._root);
        if (rootStyle.position === 'static') {
            this._root.style.position = 'relative';
        }
        this._tooltipNode = node;
        return node;
    }

    _showTooltip(d, event) {
        const content = this.renderTooltipContent(d);
        if (content == null) return;
        const tip = this._ensureTooltip();
        const tipNode = tip.node();
        // Replace previous content.
        tipNode.innerHTML = '';
        if (typeof content === 'string') {
            tipNode.innerHTML = content;
        } else if (content instanceof Node) {
            tipNode.appendChild(content);
        } else {
            // Unexpected return type — coerce to string for safety.
            tipNode.textContent = String(content);
        }
        tip.style('visibility', 'visible');
        this._moveTooltip(event);
    }

    _moveTooltip(event) {
        if (!this._tooltipNode) return;
        const tip = this._tooltipNode;
        const rootRect = this._root.getBoundingClientRect();
        const tipNode = tip.node();
        const tipW = tipNode.offsetWidth;
        const tipH = tipNode.offsetHeight;

        // Position relative to root container.
        let x = event.clientX - rootRect.left + 12;
        let y = event.clientY - rootRect.top + 12;

        // Keep tooltip inside container horizontally.
        if (x + tipW > rootRect.width) x = Math.max(0, rootRect.width - tipW - 4);
        if (y + tipH > rootRect.height) y = Math.max(0, event.clientY - rootRect.top - tipH - 8);

        tip.style('left', x + 'px').style('top', y + 'px');
    }

    _hideTooltip() {
        if (this._tooltipNode) this._tooltipNode.style('visibility', 'hidden');
    }
}